/**
 * Admin Content Editor Page
 *
 * Allows super admins to view and edit dynamic content sections
 * that are displayed throughout the Re:Nav-96 website.
 *
 * Features:
 * - View all content sections
 * - Edit content with rich text editor
 * - Toggle section active status
 * - Save changes to database
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth, useIsSuperAdmin } from '@/hooks/useAuth';
import { contentApi, ApiClientError } from '@/services/api';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import type { DynamicContent } from '@/types';

/**
 * Section card display component
 */
interface SectionCardProps {
  section: DynamicContent;
  isSelected: boolean;
  onClick: () => void;
}

function SectionCard({ section, isSelected, onClick }: SectionCardProps) {
  // Format section key for display
  const displayName = section.sectionKey
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-lg border transition-all ${
        isSelected
          ? 'border-primary bg-primary/10'
          : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-white">{section.title || displayName}</h3>
        <span
          className={`px-2 py-0.5 text-xs rounded ${
            section.isActive
              ? 'bg-green-500/20 text-green-400'
              : 'bg-red-500/20 text-red-400'
          }`}
        >
          {section.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>
      <p className="text-xs text-white/40">{section.sectionKey}</p>
      {section.updatedAt && (
        <p className="text-xs text-white/30 mt-1">
          Updated: {new Date(section.updatedAt).toLocaleDateString()}
        </p>
      )}
    </button>
  );
}

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <svg
        className="animate-spin h-8 w-8 text-primary"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}

/**
 * Admin Content Editor Page Component
 */
export function ContentEditorPage() {
  const navigate = useNavigate();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const isSuperAdmin = useIsSuperAdmin();

  // State
  const [sections, setSections] = useState<DynamicContent[]>([]);
  const [selectedSection, setSelectedSection] = useState<DynamicContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Editor state
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  /**
   * Fetch all content sections
   */
  const fetchSections = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await contentApi.list();
      setSections(data);

      // Auto-select first section if none selected
      if (data.length > 0 && !selectedSection) {
        const first = data[0];
        setSelectedSection(first);
        setEditTitle(first.title || '');
        setEditContent(first.htmlContent || '');
        setEditIsActive(first.isActive);
      }
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to load content sections';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSection]);

  /**
   * Initial load
   */
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchSections();
    }
  }, [authLoading, isAuthenticated, fetchSections]);

  /**
   * Redirect non-admin users
   */
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isSuperAdmin)) {
      navigate('/login', { state: { from: '/admin/content' }, replace: true });
    }
  }, [authLoading, isAuthenticated, isSuperAdmin, navigate]);

  /**
   * Handle section selection
   */
  const handleSelectSection = useCallback((section: DynamicContent) => {
    if (hasChanges) {
      const confirm = window.confirm('You have unsaved changes. Discard them?');
      if (!confirm) return;
    }

    setSelectedSection(section);
    setEditTitle(section.title || '');
    setEditContent(section.htmlContent || '');
    setEditIsActive(section.isActive);
    setHasChanges(false);
    setError(null);
    setSuccessMessage(null);
  }, [hasChanges]);

  /**
   * Handle title change
   */
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditTitle(e.target.value);
    setHasChanges(true);
  }, []);

  /**
   * Handle content change
   */
  const handleContentChange = useCallback((value: string) => {
    setEditContent(value);
    setHasChanges(true);
  }, []);

  /**
   * Handle active status toggle
   */
  const handleActiveToggle = useCallback(() => {
    setEditIsActive((prev) => !prev);
    setHasChanges(true);
  }, []);

  /**
   * Save changes
   */
  const handleSave = useCallback(async () => {
    if (!selectedSection) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      await contentApi.update(selectedSection.sectionKey, {
        title: editTitle || undefined,
        htmlContent: editContent,
        isActive: editIsActive,
      });

      // Update local state
      setSections((prev) =>
        prev.map((s) =>
          s.id === selectedSection.id
            ? {
                ...s,
                title: editTitle,
                htmlContent: editContent,
                isActive: editIsActive,
                updatedAt: new Date().toISOString(),
              }
            : s
        )
      );

      setSelectedSection((prev) =>
        prev
          ? {
              ...prev,
              title: editTitle,
              htmlContent: editContent,
              isActive: editIsActive,
              updatedAt: new Date().toISOString(),
            }
          : null
      );

      setHasChanges(false);
      setSuccessMessage('Content saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to save content';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [selectedSection, editTitle, editContent, editIsActive]);

  /**
   * Reset changes
   */
  const handleReset = useCallback(() => {
    if (!selectedSection) return;

    setEditTitle(selectedSection.title || '');
    setEditContent(selectedSection.htmlContent || '');
    setEditIsActive(selectedSection.isActive);
    setHasChanges(false);
  }, [selectedSection]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <LoadingSpinner />
      </div>
    );
  }

  // Don't render anything if not authorized (will redirect)
  if (!isAuthenticated || !isSuperAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">
            <span className="text-gradient">Content Editor</span>
          </h1>
          <p className="text-white/60 mt-1">
            Manage dynamic content sections for the website
          </p>
        </div>
        <Link to="/admin" className="btn-secondary">
          Back to Admin
        </Link>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Success Banner */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <p className="text-green-400">{successMessage}</p>
        </div>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : sections.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-white/60">No content sections found.</p>
          <p className="text-white/40 text-sm mt-2">
            Run the seed script to create default content sections.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sections List */}
          <div className="lg:col-span-1">
            <h2 className="text-lg font-semibold text-white/80 mb-4">
              Sections ({sections.length})
            </h2>
            <div className="space-y-3">
              {sections.map((section) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  isSelected={selectedSection?.id === section.id}
                  onClick={() => handleSelectSection(section)}
                />
              ))}
            </div>
          </div>

          {/* Editor Panel */}
          <div className="lg:col-span-3">
            {selectedSection ? (
              <div className="card">
                {/* Section Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      {selectedSection.title ||
                        selectedSection.sectionKey
                          .split('_')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')}
                    </h2>
                    <p className="text-sm text-white/40 mt-1">
                      Section key: {selectedSection.sectionKey}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Active Toggle */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-white/60">Active</span>
                      <button
                        type="button"
                        onClick={handleActiveToggle}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          editIsActive ? 'bg-primary' : 'bg-white/20'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            editIsActive ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </label>

                    {/* Save Indicator */}
                    {hasChanges && (
                      <span className="text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded">
                        Unsaved changes
                      </span>
                    )}
                  </div>
                </div>

                {/* Title Field */}
                <div className="mb-6">
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-white/80 mb-2"
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={editTitle}
                    onChange={handleTitleChange}
                    className="input"
                    placeholder="Section title (optional)"
                  />
                </div>

                {/* Content Editor */}
                <div className="mb-6">
                  <RichTextEditor
                    label="Content"
                    value={editContent}
                    onChange={handleContentChange}
                    placeholder="Enter content here... You can use HTML or the visual editor."
                    minHeight={400}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-4 pt-4 border-t border-white/10">
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={!hasChanges || isSaving}
                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <span className="flex items-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="card text-center py-12">
                <p className="text-white/60">
                  Select a content section from the list to edit
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentEditorPage;
